/**
 * Reveal-and-focus helper for form-field error UX.
 *
 * Save handlers call `revealAndFocusFirstError()` (or the lower-level
 * `revealAndFocusFormField(node)`) after surfacing validation errors so the
 * user lands on the first offending field instead of having to scan the
 * page for red borders.
 *
 * Uncollapse mechanism:
 *   Before scrolling we dispatch a bubbling `REVEAL_EVENT` from the field's
 *   DOM node. Any collapsible ancestor (FormSection, Section, …) attaches a
 *   listener for this event and expands itself when its body contains the
 *   target. The event bubbles up the tree, so a field nested several
 *   collapse layers deep ends up fully visible after a single dispatch.
 *
 *   The collapsible container's listener is responsible for triggering its
 *   own state change synchronously — we then wait one frame so React paints
 *   the expanded state before scrolling, otherwise scrollIntoView lands on
 *   the still-collapsed offset.
 */

export const REVEAL_EVENT = 'vector:revealFormField'

const ERROR_SELECTOR = '.c-fieldShell--error'

// Inputs we can usefully focus — read-only spans and section headers are skipped.
const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]),' +
  'textarea:not([disabled]),' +
  'select:not([disabled]),' +
  '[contenteditable="true"]'

export function revealAndFocusFormField(node: HTMLElement): void {
  // Bubbles up so every collapsible ancestor listening for REVEAL_EVENT
  // can decide whether to expand itself.
  node.dispatchEvent(new CustomEvent(REVEAL_EVENT, { bubbles: true }))

  // Wait one frame for the uncollapse setState to flush before measuring
  // the scroll target — otherwise we read the pre-expanded offset.
  requestAnimationFrame(() => {
    node.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const focusable = node.matches(FOCUSABLE_SELECTOR)
      ? (node as HTMLElement)
      : node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
    focusable?.focus({ preventScroll: true })
  })
}

/**
 * Locates the first `.c-fieldShell--error` in document order and reveals/focuses it.
 * Returns true if a target was found (caller can use this to decide whether
 * to surface any additional feedback when nothing was actually visible).
 */
export function revealAndFocusFirstError(root: ParentNode = document): boolean {
  const target = root.querySelector<HTMLElement>(ERROR_SELECTOR)
  if (!target) return false
  revealAndFocusFormField(target)
  return true
}
